import { useQuery } from '@apollo/client';
import type { FeedItem } from '@knocklabs/client';
import type { GenericData } from '@knocklabs/types/src/common';
import { GetNotificationListDetailsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useMessagesFeedStore } from 'src/components/messages/useMessagesFeedStore';

import type { NotificationItem } from './knockContent';
import { buildNotificationItem } from './knockContent';
import type {
  Lookup,
  NotificationLookupData,
} from './notification-types/types';

export type { NotificationItem };

export const useNotificationItems = (): {
  loading: boolean;
  items: NotificationItem[];
} => {
  const items = useMessagesFeedStore((state) => state.items);
  const feedLoading = useMessagesFeedStore((state) => state.loading);

  const variables = useMemo(
    () => ({
      issueIds: getUniqueIds(items, 'objectId').concat(
        getUniqueIds(items, 'issueId')
      ),
      riskIds: getUniqueIds(items, 'objectId'),
      controlIds: getUniqueIds(items, 'objectId'),
      actionIds: getUniqueIds(items, 'objectId').concat(
        getUniqueIds(items, 'actionId')
      ),
      documentFileIds: getUniqueIds(items, 'objectId'),
      documentIds: getUniqueIds(items, 'objectId'),
      indicatorIds: getUniqueIds(items, 'objectId'),
    }),
    [items]
  );

  const { loading: notificationListDetailsLoading, data } = useQuery(
    GetNotificationListDetailsDocument,
    {
      variables,
      skip:
        variables.actionIds.length === 0 &&
        variables.riskIds.length === 0 &&
        variables.controlIds.length === 0 &&
        variables.issueIds.length === 0 &&
        variables.documentIds.length === 0 &&
        variables.indicatorIds.length === 0 &&
        variables.documentFileIds.length === 0,
    }
  );

  const lookupData: NotificationLookupData = useMemo(() => {
    return {
      issues: createLookup(data?.issue),
      risks: createLookup(data?.risk),
      controls: createLookup(data?.control),
      actions: createLookup(data?.action),
      documentFiles: createLookup(data?.document_file),
      documents: createLookup(data?.document),
      indicators: createLookup(data?.indicator),
    };
  }, [data]);

  const newItems: NotificationItem[] = useMemo(
    () =>
      items
        .map((item) => buildNotificationItem(item, lookupData))
        .filter((item): item is NotificationItem => item !== null),
    [items, lookupData]
  );

  return {
    loading: feedLoading || notificationListDetailsLoading,
    items: newItems,
  };
};

function createLookup<T extends { Id: string }>(
  data: T[] | undefined
): Lookup<T> | undefined {
  if (!data) {
    return undefined;
  }

  return data.reduce<Lookup<T>>((previous, dataItem) => {
    previous[dataItem.Id] = dataItem;

    return previous;
  }, {});
}

function onlyUnique<T>(value: T, index: number, array: T[]) {
  return array.indexOf(value) === index;
}

function getUniqueIds(items: FeedItem<GenericData>[], key: string): string[] {
  return items
    .filter((i) => i.data && key in i.data)
    .map((i) => i.data![key] as string)
    .filter(onlyUnique);
}
